import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Chip,
    TextField,
    Select,
    MenuItem,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useNotify } from "react-admin";
import { API } from "../../Constant";
import { getConfigGet, getConfigPost } from "../../getConfig";

const SHOP_GRID = API + "/admin/shop-grid";

export interface SpendMetric {
    field: "uk_spend" | "sprive_sales";
    title: string;
    windowLabel: string;
    promptTitle: string;
    prompt: string;
}

const csvCell = (v: any) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const SpendManager = ({ metrics }: { metrics: SpendMetric[] }) => {
    const notify = useNotify();
    const [idx, setIdx] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);

    const m = metrics[idx];

    const copyPrompt = () => {
        try {
            navigator.clipboard.writeText(m.prompt);
            notify("Prompt copied", "info");
        } catch (e) {
            notify("Copy failed — select the text manually", "warning");
        }
    };

    const download = async () => {
        try {
            setDownloading(true);
            const res: any = await axios.get(SHOP_GRID, getConfigGet({ resource: "catalog" }));
            const rows: any[] = Array.isArray(res.data) ? res.data : res.data?.data || [];
            const header = ["brand", "provider", "status", m.field];
            const lines = [header.join(",")];
            rows.forEach((r) =>
                lines.push([r.brand, r.provider, r.status, r[m.field] ?? ""].map(csvCell).join(","))
            );
            const blob = new Blob([lines.join("\n")], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${m.field}_${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            notify("Failed to download brand list", "warning");
        } finally {
            setDownloading(false);
        }
    };

    const upload = async (file: File) => {
        try {
            setUploading(true);
            setResult(null);
            const text = await file.text();
            const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
            if (!lines.length) {
                notify("Empty file", "warning");
                return;
            }
            // Accept comma or tab delimited (Excel/Sheets often export TSV).
            const delimiter = lines[0].includes("\t") ? "\t" : ",";
            const header = lines[0].split(delimiter).map((h) => h.trim().toLowerCase());
            const brandIdx = header.indexOf("brand");
            const valueIdx = header.indexOf(m.field);
            if (brandIdx === -1 || valueIdx === -1) {
                notify(`CSV must have "brand" and "${m.field}" columns`, "warning");
                return;
            }
            const updates = lines.slice(1).map((line) => {
                const cols = line.split(delimiter);
                return {
                    brand: (cols[brandIdx] || "").trim().replace(/^"|"$/g, ""),
                    value: (cols[valueIdx] || "").trim(),
                };
            });
            const res: any = await axios.post(
                SHOP_GRID,
                { resource: "spend", field: m.field, updates },
                { headers: getConfigPost() }
            );
            setResult(res.data);
            notify(`Imported: ${res.data.upserted} upserted, ${res.data.skipped} skipped`, "info");
        } catch (e: any) {
            notify(e?.response?.data?.message || "Upload failed", "error");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gridGap={12} mb={1}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Metric:
                </Typography>
                <Select
                    value={idx}
                    onChange={(e) => {
                        setIdx(Number(e.target.value));
                        setResult(null);
                    }}
                >
                    {metrics.map((mm, i) => (
                        <MenuItem key={mm.field} value={i}>
                            {mm.title} ({mm.windowLabel})
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
                1) Download the full brand list (all providers &amp; statuses). 2) Fill the{" "}
                <b>{m.field}</b> column. 3) Upload it back.
            </Typography>

            <Box display="flex" alignItems="center" gridGap={12} mt={1}>
                <Button
                    variant="outlined"
                    startIcon={downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                    disabled={downloading}
                    onClick={download}
                >
                    Download brand list
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    disabled={uploading}
                >
                    Upload filled CSV
                    <input
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) upload(f);
                            e.target.value = "";
                        }}
                    />
                </Button>
                {result && (
                    <Chip
                        label={`${result.upserted} upserted · ${result.skipped} skipped`}
                    />
                )}
            </Box>

            <Box mt={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                        {m.promptTitle}
                    </Typography>
                    <Button size="small" startIcon={<FileCopyIcon fontSize="small" />} onClick={copyPrompt}>
                        Copy prompt
                    </Button>
                </Box>
                <TextField
                    fullWidth
                    multiline
                    size="small"
                    variant="outlined"
                    value={m.prompt}
                    InputProps={{ readOnly: true, style: { fontSize: 12, fontFamily: "monospace" } }}
                />
            </Box>
        </Box>
    );
};

export default SpendManager;
